import React, { useEffect, useRef, useState } from "react";
import { Record, PayCode, Period, PayAmount } from "../types";
import MaterialIcon from "./MaterialIcon";

interface ManageRecordFormProps {
  handleSubmit: (record: Record) => void;
  period: Period;
  record: Record | null;
  records: Record[];
}

function ManageRecordForm({
  handleSubmit,
  period,
  record,
  records,
}: ManageRecordFormProps) {
  let dateInput = useRef<HTMLInputElement | null>(null);
  let [date, setDate] = useState<string>("");
  let [amount, setAmount] = useState<PayAmount>(0.25);
  let [code, setCode] = useState<PayCode>("H");
  let [description, setDescription] = useState<string>("");

  useEffect(() => {
    if (record === null) {
      return;
    }
    setDate(record.date);
    setAmount(record.amount);
    setCode(record.code);
    setDescription(record.description);
  }, [record]);

  function isInPeriod(period: Period, date: string): boolean {
    let startDate = new Date(period.start_date);
    let selectedDate = new Date(date);

    if (selectedDate < startDate) {
      return false;
    }

    if (period.end_date) {
      let endDate = new Date(period.end_date);
      if (selectedDate > endDate) {
        return false;
      }
    }

    return true;
  }

  function recordExistsOnDate(date: string) {
    for (let existing_record of records) {
      if (existing_record.date === date) {
        if (record && record.id === existing_record.id) {
          continue;
        }
        return true;
      }
    }
    return false;
  }

  async function validateAndSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      if (!isInPeriod(period, date)) {
        dateInput.current &&
          dateInput.current.setCustomValidity(
            "Selected date is outside of the current period start or end!",
          );
        dateInput.current?.reportValidity();
        return;
      }

      if (recordExistsOnDate(date)) {
        dateInput.current &&
          dateInput.current.setCustomValidity(
            "Record already exists on this date!",
          );
        dateInput.current?.reportValidity();
        return;
      }

      handleSubmit({
        id: record ? record.id : -1, // Use dummy IDs to make TypeScript happy.
        period_id: record ? record.period_id : -1, // We will replace the period ID before sending the record to the server.
        date: date,
        amount: amount,
        description: description,
        code,
      });
      setDate("");
      setAmount(0.25);
      setCode("H");
      setDescription("");
    } catch (err) {
      alert(err);
    }
  }

  return (
    <>
      <form className="flex flex-col" onSubmit={validateAndSubmit}>
        <label className="mb-1 mt-2">Date:</label>
        <input
          id="date"
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            dateInput.current?.setCustomValidity("");
          }}
          className="text-grayscale-900 shadow-grayscale-900 rounded-lg border-2 p-2 shadow-md focus:invalid:border-red-500"
          required
          ref={dateInput}
        />
        <label className="mb-1 mt-2">Hours:</label>
        <select
          name="amount"
          value={amount}
          onChange={(e) => {
            let v = parseFloat(e.target.value);
            if (v === 0.25 || v === 0.5 || v === 0.75 || v === 1) {
              setAmount(v);
            }
          }}
          className="text-grayscale-900 shadow-grayscale-900 rounded-lg border-2 p-2 shadow-md"
        >
          <option value="0.25">1/4 Day</option>
          <option value="0.5">1/2 Day</option>
          <option value="0.75">3/4 Day</option>
          <option value="1">Full Day</option>
        </select>
        <label className="mb-1 mt-2">Code:</label>
        <select
          name="code"
          value={code}
          onChange={(e) => {
            // There must be a more elegant way of checking if a value/input is in the type union
            // but I don't know what it is.
            // THIS MUST BE UPDATED IF THE UNION TYPES CHANGE!
            if (e.target.value === "A" || e.target.value === "H") {
              setCode(e.target.value);
            }
          }}
          className="text-grayscale-900 shadow-grayscale-900 rounded-lg border-2 p-2 shadow-md"
        >
          <option value="A">A (OSW)</option>
          <option value="H">H (Routine)</option>
        </select>
        <label className="mb-1 mt-2">Justification</label>
        <textarea
          id="description"
          value={description}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
          className="text-grayscale-900 shadow-grayscale-900 rounded-lg border-2 p-2 shadow-md focus:invalid:border-red-500"
          required
        />
        <button
          className="from-blu-200 to-blu-600 mt-4 flex items-baseline justify-center rounded-lg bg-gradient-to-tr p-2"
          type="submit"
        >
          <MaterialIcon icon={record === null ? "add" : "save"} />
        </button>
      </form>
    </>
  );
}

export default ManageRecordForm;
